import { client } from '@/lib/api/client'
import {
  venueCourtPriceBandRemainingPath,
  venueCourtPriceBandsPath,
  venueCourtPriceDatePath,
  venueCourtPriceDatesPath,
} from '@/lib/api/config'
import { toApiError } from '@/lib/api/errors'

import type {
  CourtPriceBandSaveInput,
  CourtPriceBandView,
  CourtPriceDateListView,
  CourtPriceDateSaveInput,
  CourtPriceDayType,
  CourtRemainingWindowsView,
} from './priceTypes'

/**
 * `GET .../price-bands` (#852 §2-1).
 * 🚨 게이팅 대상이 아니다 — 이용권이 만료돼도 항상 200(무엇이 잠겼는지는 `editable` 로 안다).
 */
export async function fetchCourtPriceBands(
  venueId: string,
  courtId: number
): Promise<CourtPriceBandView> {
  try {
    const { data } = await client.get<CourtPriceBandView>(venueCourtPriceBandsPath(venueId, courtId))
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `PUT .../price-bands` (#852 §2-2).
 * 🚨 전체 치환 — `input.bands` 에 없는 구간은 삭제된다. 응답은 `GET` 과 **동일한 View** 라 재조회하지 않는다.
 * 🚨 같은 값을 다시 보내면 서버가 저장하지 않는다(멱등) — 일괄 적용 재시도가 안전한 근거.
 */
export async function saveCourtPriceBands(
  venueId: string,
  courtId: number,
  input: CourtPriceBandSaveInput
): Promise<CourtPriceBandView> {
  try {
    const { data } = await client.put<CourtPriceBandView>(
      venueCourtPriceBandsPath(venueId, courtId),
      input
    )
    return data
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../price-dates?from=&to=` (#866 API ④). 게이팅 대상이 아니다.
 *
 * 🚨 이 EP 의 응답 형태는 허브 `outputs/api-spec.md` 미게시 상태의 **가정**이다(설계 R1) — `dates` 가
 * 없거나 배열이 아니어도 화면이 죽지 않도록 여기서 한 번 정규화한다.
 */
export async function fetchCourtPriceDates(
  venueId: string,
  courtId: number,
  from: string,
  to: string
): Promise<CourtPriceDateListView> {
  try {
    const { data } = await client.get<CourtPriceDateListView>(
      venueCourtPriceDatesPath(venueId, courtId),
      { params: { from, to } }
    )
    return { ...data, dates: Array.isArray(data?.dates) ? data.dates : [] }
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `PUT .../price-dates/{date}` (#866 API ④). 그 날짜 전량 교체·멱등. 게이팅 대상(404→403→409).
 *
 * 🚨 **응답을 소비하지 않는다.** 저장 응답의 형태가 아직 계약 문서로 확정되지 않아(R1) 캐시에 직접
 * 꽂으면 게시본과 어긋날 때 화면이 조용히 잘못된 값을 보여 준다 — 대신 목록 쿼리를 무효화한다
 * (요일유형 축은 게시본이 있어 응답을 그대로 캐시에 넣는 것과 의도적으로 다르다).
 */
export async function saveCourtPriceDate(
  venueId: string,
  courtId: number,
  date: string,
  input: CourtPriceDateSaveInput
): Promise<void> {
  try {
    await client.put(venueCourtPriceDatePath(venueId, courtId, date), input)
  } catch (error) {
    throw toApiError(error)
  }
}

/** `DELETE .../price-dates/{date}` (#866 API ④). 그날 규칙을 지우면 요일유형 규칙으로 되돌아간다. */
export async function deleteCourtPriceDate(
  venueId: string,
  courtId: number,
  date: string
): Promise<void> {
  try {
    await client.delete(venueCourtPriceDatePath(venueId, courtId, date))
  } catch (error) {
    throw toApiError(error)
  }
}

/**
 * `GET .../price-bands/remaining-windows?dayType=|date=` (#866 API ⑤).
 * 🚨 `dayType`·`date` 중 **하나만** 보낸다(동시 지정은 400). 이용권이 만료돼도 200.
 * 🚨 편집 중인 선택지 계산에는 쓰지 않는다 — 서버는 저장되지 않은 편집을 모른다(설계 D6).
 */
export async function fetchCourtRemainingWindows(
  venueId: string,
  courtId: number,
  axis: { dayType: CourtPriceDayType } | { date: string }
): Promise<CourtRemainingWindowsView> {
  try {
    const { data } = await client.get<CourtRemainingWindowsView>(
      venueCourtPriceBandRemainingPath(venueId, courtId),
      { params: axis }
    )
    return { ...data, windows: Array.isArray(data?.windows) ? data.windows : [] }
  } catch (error) {
    throw toApiError(error)
  }
}
